import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopBar } from "@/components/TopBar";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowLeft, Flame, BookOpen, CheckCircle2, Circle, Cross } from "lucide-react";

export default function ReadingPlanPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isParent = user?.role === "Parent";
  const [students, setStudents] = useState([]);
  const [sid, setSid] = useState("");
  const [data, setData] = useState(null);

  const targetId = isParent ? sid : user?.id;

  const load = useCallback(() => {
    if (!targetId) return;
    api.get(`/reading-plan/${targetId}`).then((r) => setData(r.data));
  }, [targetId]);

  useEffect(() => {
    if (isParent) api.get("/students").then((r) => { setStudents(r.data); if (r.data[0]) setSid(r.data[0].id); });
  }, [isParent]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (day) => {
    if (isParent) return;
    const { data: res } = await api.put(`/reading-plan/${targetId}/day/${day}`);
    if (res.completed) toast.success("Great job reading today! 🌱");
    load();
  };

  const pct = data ? Math.round((data.stats.completed / data.stats.total) * 100) : 0;

  return (
    <div>
      <TopBar subtitle="Bible Reading Plan" />
      <main className="max-w-2xl mx-auto px-6 md:px-8 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <button onClick={() => navigate(isParent ? "/parent" : "/student")} className="flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#3D405B]"><ArrowLeft size={16} /> Back</button>
          {isParent && (
            <select data-testid="plan-student" value={sid} onChange={(e) => setSid(e.target.value)}
              className="rounded-full border border-[#EAE6DF] bg-white px-5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#E07A5F]">
              {students.map((s) => <option key={s.id} value={s.id}>{s.first_name}</option>)}
            </select>
          )}
        </div>

        {data && (
          <>
            <div className="relative grain rounded-3xl overflow-hidden p-8 text-white mb-8" style={{ background: "#3D405B" }}>
              <div className="flex items-center gap-2 text-white/80"><Cross size={16} /><span className="text-xs font-semibold uppercase tracking-[0.2em]">Reading Plan</span></div>
              <h1 className="font-head text-3xl font-bold mt-2">{data.title}</h1>
              <div className="flex items-center gap-6 mt-5">
                <div className="flex items-center gap-2"><Flame size={20} className="text-[#F4A261]" /><span className="stat text-2xl font-bold">{data.stats.streak}</span><span className="text-white/70 text-sm">day streak</span></div>
                <div className="flex items-center gap-2"><CheckCircle2 size={20} className="text-[#81B29A]" /><span className="stat text-2xl font-bold">{data.stats.completed}/{data.stats.total}</span></div>
              </div>
              <div className="h-2 rounded-full bg-white/20 overflow-hidden mt-4">
                <div className="h-full rounded-full bg-[#81B29A] transition-all" style={{ width: `${pct}%` }} />
              </div>
            </div>

            <div className="space-y-2">
              {data.days.map((d, i) => {
                const isToday = d.day === data.stats.today_index;
                return (
                  <motion.button key={d.day} data-testid={`plan-day-${d.day}`}
                    initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
                    onClick={() => toggle(d.day)} disabled={isParent}
                    className={`w-full flex items-center gap-4 rounded-2xl border p-4 text-left transition-colors ${d.completed ? "bg-[#81B29A]/10 border-[#81B29A]/40" : isToday ? "bg-white border-[#E07A5F]" : "bg-white border-[#EAE6DF]"} ${!isParent && "hover:border-[#81B29A]"}`}>
                    {d.completed ? <CheckCircle2 size={22} className="text-[#81B29A] shrink-0" /> : <Circle size={22} className="text-[#D8D2C8] shrink-0" />}
                    <span className="flex-1">
                      <span className="flex items-center gap-2">
                        <span className="stat text-xs text-[#6B7280]">Day {d.day}</span>
                        {isToday && !d.completed && <span className="text-[10px] font-bold uppercase tracking-wide text-white bg-[#E07A5F] px-2 py-0.5 rounded-full">Today</span>}
                      </span>
                      <span className="block font-head font-semibold text-[#3D405B]">{d.reference}</span>
                      <span className="block text-sm text-[#6B7280]">{d.theme}</span>
                    </span>
                    <BookOpen size={18} className="text-[#6B7280] shrink-0" />
                  </motion.button>
                );
              })}
            </div>
            {isParent && <p className="text-center text-xs text-[#6B7280] mt-6">Viewing {students.find((s) => s.id === sid)?.first_name}'s progress. Only the student can check off readings.</p>}
          </>
        )}
      </main>
    </div>
  );
}
